import {
  CLCreativeType,
  Creative,
  DynamicParameter,
  RemoteCreative,
  CreativeFormat,
  RemoteDynamicParameter,
  RemoteWidgetDP,
  TemplateEssentials,
  RemoteQRCodeDP,
  QRCodeDPValue,
  RemoteSubtitlesDP,
  SubtitlesDPValue,
} from './creatives.types';
import {CLEnum, CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import mapValues from 'lodash/mapValues';
import {DateTime} from 'luxon';
import _isNil from 'lodash/isNil';
import {ProductType, ProductTypeName} from '../products/products.enums';
import {DynamicParametersType} from '../dynamicParameters/dynamicParameters.enums';
import getWidget from '../dynamicParameters/widgets/utils/getWidget';
import {
  CreativeTemplateSource,
  CreativeTemplateSourceType,
  CreativeTemplateType,
  RemoteCreativeTemplate,
} from '../creativeTemplates/creativeTemplates.types';

function toClientDynamicParameter(
  paramKey: string,
  parameter: RemoteDynamicParameter,
  templateEnums: Record<string, CLEnumsMapper>,
): DynamicParameter {
  return {
    ...parameter,
    paramKey,
    dynamicCategory: parameter.dynamicCategory
      ? parameter.dynamicCategory
      : templateEnums[CLEnumKeys.Templates.DynamicParameters].byValue[parameter.category].name,
    values: Object.fromEntries(
      Object.entries(parameter.values).map(([key, {v, w, h, dpSourceType, ...props}]) => [
        key,
        {
          ...props,
          value:
            [
              DynamicParametersType.Widget,
              DynamicParametersType.QR,
              DynamicParametersType.Subtitles,
            ].includes(parameter.type) && v
              ? JSON.stringify(v)
              : v,
          width: w,
          height: h,
          dynamicSourceType: dpSourceType ? dpSourceType[0] : null,
          isDynamic: !!dpSourceType?.length,
        },
      ]),
    ),
  };
}

function toRemoteDynamicParameter(param: DynamicParameter): RemoteDynamicParameter {
  if (param.type === DynamicParametersType.Widget) {
    return {
      ...param,
      type: DynamicParametersType.Widget,
      values: mapValues(param.values, ({width, height, value, dynamicSourceType, ...props}) => {
        let dpValue: RemoteWidgetDP['values'][string]['v'] = null;

        if (value) {
          const {id, args, uiName, scriptUrl} = getWidget(value);
          dpValue = {id, args, uiName, scriptUrl};
        }

        return {
          w: width,
          h: height,
          v: dpValue,
          dpSourceType: _isNil(dynamicSourceType) ? null : [dynamicSourceType],
          ...props,
        };
      }),
    };
  } else if (param.type === DynamicParametersType.QR) {
    return {
      ...param,
      type: DynamicParametersType.QR,
      values: mapValues(param.values, ({width, height, value, dynamicSourceType, ...props}) => {
        let dpValue: RemoteQRCodeDP['values'][string]['v'] = null;

        if (value) {
          dpValue = JSON.parse(value) as QRCodeDPValue;
        }

        return {
          w: width,
          h: height,
          v: dpValue,
          dpSourceType: _isNil(dynamicSourceType) ? null : [dynamicSourceType],
          ...props,
        };
      }),
    };
  } else if (param.type === DynamicParametersType.Subtitles) {
    return {
      ...param,
      type: DynamicParametersType.Subtitles,
      values: mapValues(param.values, ({width, height, value, dynamicSourceType, ...props}) => {
        let dpValue: RemoteSubtitlesDP['values'][string]['v'] = null;

        if (value) {
          dpValue = JSON.parse(value) as SubtitlesDPValue;
        }

        return {
          w: width,
          h: height,
          v: dpValue,
          dpSourceType: _isNil(dynamicSourceType) ? null : [dynamicSourceType],
          ...props,
        };
      }),
    };
  }

  return {
    ...param,
    type: param.type as Exclude<DynamicParametersType, DynamicParametersType.Widget>,
    values: mapValues(param.values, ({width, height, value, dynamicSourceType, ...props}) => ({
      w: width,
      h: height,
      v: value,
      dpSourceType: _isNil(dynamicSourceType) ? null : [dynamicSourceType],
      ...props,
    })),
  };
}

function toRemoteDynamicParameters(
  params: DynamicParameter[],
): Record<string, RemoteDynamicParameter> {
  return Object.fromEntries(
    params.map((param: DynamicParameter) => [param.paramKey, toRemoteDynamicParameter(param)]),
  );
}

function toClientDynamicParameters(
  params: Record<string, RemoteDynamicParameter> | undefined,
  templateEnums: Record<string, CLEnumsMapper>,
): Record<string, DynamicParameter> {
  return Object.fromEntries(
    Object.entries<RemoteDynamicParameter>(params || {}).map(([key, value]) => [
      key,
      toClientDynamicParameter(key, value, templateEnums),
    ]),
  );
}

function convertSourceToClient(source: CreativeTemplateSourceType): CreativeTemplateSource {
  const sourceMapping: Record<CreativeTemplateSourceType, CreativeTemplateSource> = {
    [CreativeTemplateSourceType.AfterEffect]: CreativeTemplateSource.AfterEffect,
    [CreativeTemplateSourceType.ClinchSDK]: CreativeTemplateSource.ClinchSDK,
    [CreativeTemplateSourceType.AdBuilder]: CreativeTemplateSource.AdBuilder,
  };

  return sourceMapping[source];
}

function toClientCreative(
  generalEnums: Record<string, CLEnumsMapper>,
  templateEnums: Record<string, CLEnumsMapper>,
  remoteCreative: RemoteCreative,
): Creative {
  remoteCreative.baseStyle = (remoteCreative.baseStyle ||
    remoteCreative.template) as RemoteCreativeTemplate;

  const templateEssentials: TemplateEssentials = {
    ...remoteCreative.baseStyle,
    clientShareInfo:
      remoteCreative.baseStyle.clientShareInfo || remoteCreative.baseStyle.sourceToTargetClients,
    features: remoteCreative.features,
    formats: remoteCreative.formats.map(
      ({minItemCount, maxItemCount: itemCount}: CreativeFormat) => ({
        minItemCount,
        itemCount,
      }),
    ),
    minItemsCount: remoteCreative.formats[0]?.minItemCount || 0,
    maxItemsCount: remoteCreative.formats[0]?.maxItemCount || 0,
    type: remoteCreative.baseStyle?.type ?? (remoteCreative.type as CreativeTemplateType),
    productType: remoteCreative.baseStyle?.productType ?? remoteCreative.productType,
    source:
      typeof remoteCreative.baseStyle?.source === 'number'
        ? convertSourceToClient(remoteCreative.baseStyle?.source as CreativeTemplateSourceType)
        : remoteCreative.baseStyle.source,
  };

  return {
    ...remoteCreative,
    type: generalEnums[CLEnumKeys.General.CreativeType].byValue[
      remoteCreative.type
    ] as CLEnum<CLCreativeType>,
    productType: generalEnums[CLEnumKeys.General.ProductType].byValue[
      remoteCreative.productType ?? templateEssentials.productType
    ] as CLEnum<ProductTypeName, ProductType>,
    template: templateEssentials,
    dynamicParameters: toClientDynamicParameters(remoteCreative.dynamicParameters, templateEnums),
    updateTime: remoteCreative.updateTime ? DateTime.fromISO(remoteCreative.updateTime) : undefined,
    createTime: remoteCreative.createTime ? DateTime.fromISO(remoteCreative.createTime) : undefined,
  };
}

export {
  toClientCreative,
  toRemoteDynamicParameters,
  toClientDynamicParameters,
  toRemoteDynamicParameter,
};
